"use client";

import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { type Address, erc20Abi, isAddress, getAddress, formatUnits } from "viem";
import { ChainId, type ChainType } from "@/constants/chain-specific";
import { DEX_LIST } from "@/constants/project-specific/farms/dex";
import type { DexConfig } from "@/constants/project-specific/farms/types";
import type { LiquidityPosition } from "@/hooks/liquidity/useLiquidityPositions";

// UniswapV2Pair ABI for reading pair data
const uniswapV2PairAbi = [{
  constant: true,
  inputs: [],
  name: "token0",
  outputs: [{
    name: "",
    type: "address"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "token1",
  outputs: [{
    name: "",
    type: "address"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "getReserves",
  outputs: [{
    name: "_reserve0",
    type: "uint112"
  }, {
    name: "_reserve1",
    type: "uint112"
  }, {
    name: "_blockTimestampLast",
    type: "uint32"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "totalSupply",
  outputs: [{
    name: "",
    type: "uint256"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "name",
  outputs: [{
    name: "",
    type: "string"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "symbol",
  outputs: [{
    name: "",
    type: "string"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "decimals",
  outputs: [{
    name: "",
    type: "uint8"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [{
    name: "owner",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    name: "",
    type: "uint256"
  }],
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "factory",
  outputs: [{
    name: "",
    type: "address"
  }],
  type: "function"
}] as const;
export interface TokenInfo {
  address: Address;
  symbol: string;
  name: string;
  decimals: number;
}
export interface LiquidityPairData {
  pairAddress: Address;
  token0: TokenInfo;
  token1: TokenInfo;
  reserves: {
    reserve0: bigint;
    reserve1: bigint;
    blockTimestampLast: number;
  };
  totalSupply: bigint;
  lpName: string;
  lpSymbol: string;
  lpDecimals: number;
  userBalance?: bigint;
  factoryAddress?: Address;
  dex?: DexConfig;
  dexSlug?: string;
  dexName?: string;
  createdAt?: number; // Unix timestamp
  // Computed user amounts based on balance and reserves
  userToken0Amount?: bigint;
  userToken1Amount?: bigint;
  token0AmountFormatted?: string;
  token1AmountFormatted?: string;
  liquidityFormatted?: string;
}

/**
 * Convert LiquidityPairData to a full LiquidityPosition object
 * This creates a position object compatible with RemoveLiquidityView and other components
 */
export function pairDataToPosition(data: LiquidityPairData, chainId: ChainType): LiquidityPosition {
  return {
    id: `${data.dexSlug || "unknown"}-${data.pairAddress}-${chainId}`,
    dex: data.dexSlug || "unknown",
    dexName: data.dexName || data.dex?.name || "Unknown DEX",
    token0: {
      name: data.token0.name,
      symbol: data.token0.symbol,
      address: data.token0.address,
      decimals: data.token0.decimals,
      logo: `/images/tokens/${data.token0.symbol.toLowerCase()}.png`
    },
    token1: {
      name: data.token1.name,
      symbol: data.token1.symbol,
      address: data.token1.address,
      decimals: data.token1.decimals,
      logo: `/images/tokens/${data.token1.symbol.toLowerCase()}.png`
    },
    liquidity: (data.userBalance ?? 0n).toString(),
    liquidityFormatted: data.liquidityFormatted ?? "0",
    token0Amount: data.token0AmountFormatted ?? "0",
    token1Amount: data.token1AmountFormatted ?? "0",
    token0AmountFormatted: data.token0AmountFormatted ?? "0",
    token1AmountFormatted: data.token1AmountFormatted ?? "0",
    fees0: "0",
    fees1: "0",
    fees0Formatted: "0",
    fees1Formatted: "0",
    totalValueUsd: 0,
    // Would need price calculation
    feesValueUsd: 0,
    apr: 0,
    range: {
      tickLower: -887220,
      // V2 is full range
      tickUpper: 887220,
      currentTick: 0,
      inRange: true,
      // V2 is always in range
      lowerPrice: "0",
      upperPrice: "∞",
      currentPrice: "0"
    },
    version: "v2",
    chainId,
    contractAddress: data.pairAddress,
    pair: data.pairAddress,
    pairAddress: data.pairAddress
  } as LiquidityPosition;
}
interface BlockscoutContractCreation {
  contractAddress: string;
  contractCreator: string;
  contractFactory: string;
  creationBytecode: string;
  txHash: string;
  timestamp?: string;
}
interface BlockscoutResponse {
  status: string;
  message: string;
  result: BlockscoutContractCreation[];
}

/**
 * Get the Blockscout API base URL for a chain
 */
function getBlockscoutApiUrl(chainId: ChainType): string | null {
  switch (chainId) {
    case ChainId.SHIBARIUM:
      return "https://shibariumscan.io/api";
    case ChainId.DOGECHAIN:
      return "https://explorer.dogechain.dog/api";
    default:
      return null;
  }
}

/**
 * Fetch contract creation info from Blockscout API
 */
async function fetchContractCreation(pairAddress: Address, chainId: ChainType): Promise<{
  factoryAddress: Address | null;
  createdAt: number | null;
}> {
  const apiUrl = getBlockscoutApiUrl(chainId);
  if (!apiUrl) {
    return {
      factoryAddress: null,
      createdAt: null
    };
  }
  try {
    const response = await fetch(`${apiUrl}?module=contract&action=getcontractcreation&contractaddresses=${pairAddress}`);
    if (!response.ok) {
      console.warn(`Blockscout API request failed: ${response.status}`);
      return {
        factoryAddress: null,
        createdAt: null
      };
    }
    const data: BlockscoutResponse = await response.json();
    if (data.status === "1" && data.result && data.result.length > 0) {
      const result = data.result[0];
      const factoryAddress = result.contractFactory ? getAddress(result.contractFactory) : null;
      const createdAt = result.timestamp ? parseInt(result.timestamp, 10) : null;
      return {
        factoryAddress,
        createdAt
      };
    }
    return {
      factoryAddress: null,
      createdAt: null
    };
  } catch (error) {
    console.warn("Failed to fetch contract creation info:", error);
    return {
      factoryAddress: null,
      createdAt: null
    };
  }
}

/**
 * Match DEX by factory address
 */
function matchDexByFactory(factoryAddress: Address | null, chainId: ChainType): {
  dex: DexConfig | null;
  dexSlug: string | null;
} {
  if (!factoryAddress) {
    return {
      dex: null,
      dexSlug: null
    };
  }
  const normalizedFactory = factoryAddress.toLowerCase();
  for (const [slug, dexConfig] of Object.entries(DEX_LIST) as [string, DexConfig][]) {
    if (dexConfig.factory?.address?.toLowerCase() === normalizedFactory) {
      // Filter by chain if the DEX has chain-specific settings
      // For now, match any DEX with matching factory
      return {
        dex: dexConfig,
        dexSlug: slug
      };
    }
  }
  return {
    dex: null,
    dexSlug: null
  };
}

/**
 * Match DEX by LP name/symbol
 * Priority: 1) Exact match, 2) Partial match (contains)
 */
function matchDexByLpInfo(lpName: string, lpSymbol: string, chainId: ChainType): {
  dex: DexConfig | null;
  dexSlug: string | null;
} {
  const normalizedName = lpName.toLowerCase();
  const normalizedSymbol = lpSymbol.toLowerCase();
  let partialMatch: {
    dex: DexConfig;
    dexSlug: string;
  } | null = null;
  for (const [slug, dexConfig] of Object.entries(DEX_LIST) as [string, DexConfig][]) {
    // Skip DEXes marked as "any" or false for lpName/lpSymbol
    if (dexConfig.lpName === "any" || dexConfig.lpSymbol === "any" || dexConfig.lpName === false || dexConfig.lpSymbol === false) {
      continue;
    }
    const dexLpName = typeof dexConfig.lpName === "string" ? dexConfig.lpName.toLowerCase() : null;
    const dexLpSymbol = typeof dexConfig.lpSymbol === "string" ? dexConfig.lpSymbol.toLowerCase() : null;

    // Check for exact match first (highest priority)
    if (dexLpName && dexLpName === normalizedName || dexLpSymbol && dexLpSymbol === normalizedSymbol) {
      console.log("[matchDexByLpInfo] Exact match found:", slug);
      return {
        dex: dexConfig,
        dexSlug: slug
      };
    }

    // Check for partial match (contains) - save first match as fallback
    if (!partialMatch) {
      if (dexLpName && normalizedName.includes(dexLpName) || dexLpSymbol && normalizedSymbol.includes(dexLpSymbol) || dexLpName && dexLpName.includes(normalizedName) || dexLpSymbol && dexLpSymbol.includes(normalizedSymbol)) {
        console.log("[matchDexByLpInfo] Partial match found:", slug);
        partialMatch = {
          dex: dexConfig,
          dexSlug: slug
        };
      }
    }
  }

  // Return partial match if no exact match was found
  if (partialMatch) {
    return partialMatch;
  }
  return {
    dex: null,
    dexSlug: null
  };
}

/**
 * Hook to fetch liquidity pair data directly from the contract
 */
export function useLiquidityPairData(pairAddress: Address | string | null | undefined, chainId: ChainType | undefined, userAddress?: Address) {
  const publicClient = usePublicClient({
    chainId
  });
  return useQuery({
    queryKey: ["liquidityPairData", pairAddress, chainId, userAddress],
    queryFn: async (): Promise<LiquidityPairData | null> => {
      if (!pairAddress || !chainId || !publicClient) {
        return null;
      }

      // Validate address
      if (!isAddress(pairAddress)) {
        console.warn("Invalid pair address:", pairAddress);
        return null;
      }
      const validPairAddress = getAddress(pairAddress);
      try {
        // Fetch pair contract data in parallel
        const [token0Address, token1Address, reserves, totalSupply, lpName, lpSymbol, lpDecimals, factoryFromContract] = await Promise.all([publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "token0"
        }) as Promise<Address>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "token1"
        }) as Promise<Address>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "getReserves"
        }) as Promise<readonly [bigint, bigint, number]>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "totalSupply"
        }) as Promise<bigint>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "name"
        }).catch(() => "LP Token") as Promise<string>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "symbol"
        }).catch(() => "LP") as Promise<string>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "decimals"
        }).catch(() => 18) as Promise<number>, publicClient.readContract({
          address: validPairAddress,
          abi: uniswapV2PairAbi,
          functionName: "factory"
        }).catch(() => null) as Promise<Address | null>]);

        // Fetch token info for both tokens
        const [token0Info, token1Info] = await Promise.all([fetchTokenInfo(publicClient, token0Address), fetchTokenInfo(publicClient, token1Address)]);

        // Fetch user balance if address provided
        let userBalance: bigint | undefined;
        if (userAddress) {
          try {
            userBalance = (await publicClient.readContract({
              address: validPairAddress,
              abi: uniswapV2PairAbi,
              functionName: "balanceOf",
              args: [userAddress]
            })) as bigint;
            console.log("[useLiquidityPairData] User balance fetched:", {
              pairAddress: validPairAddress,
              userAddress,
              userBalance: userBalance.toString()
            });
          } catch (error_0) {
            console.warn("[useLiquidityPairData] Failed to fetch balance:", error_0);
            userBalance = 0n;
          }
        } else {
          console.log("[useLiquidityPairData] No userAddress provided, skipping balance fetch");
        }

        // Try to identify the DEX
        let dex: DexConfig | null = null;
        let dexSlug: string | null = null;
        let factoryAddress: Address | null = factoryFromContract as Address | null;
        let createdAt: number | null = null;

        // First try to match by LP name/symbol
        console.log("[useLiquidityPairData] Attempting DEX match:", {
          lpName,
          lpSymbol
        });
        const lpMatch = matchDexByLpInfo(lpName, lpSymbol, chainId);
        if (lpMatch.dex) {
          dex = lpMatch.dex;
          dexSlug = lpMatch.dexSlug;
          console.log("[useLiquidityPairData] Matched DEX by LP info:", dexSlug);
        }

        // If no match by LP info, try factory address from contract
        if (!dex && factoryAddress) {
          const factoryMatch = matchDexByFactory(factoryAddress, chainId);
          if (factoryMatch.dex) {
            dex = factoryMatch.dex;
            dexSlug = factoryMatch.dexSlug;
            console.log("[useLiquidityPairData] Matched DEX by factory:", dexSlug);
          }
        }

        // If still no match, try Blockscout API for factory
        if (!dex) {
          const blockscoutInfo = await fetchContractCreation(validPairAddress, chainId);
          if (blockscoutInfo.factoryAddress) {
            factoryAddress = blockscoutInfo.factoryAddress;
            createdAt = blockscoutInfo.createdAt;
            const factoryMatch_0 = matchDexByFactory(factoryAddress, chainId);
            if (factoryMatch_0.dex) {
              dex = factoryMatch_0.dex;
              dexSlug = factoryMatch_0.dexSlug;
            }
          }
        }

        // Calculate user's share of the pool if they have a balance
        let userToken0Amount: bigint | undefined;
        let userToken1Amount: bigint | undefined;
        let token0AmountFormatted: string | undefined;
        let token1AmountFormatted: string | undefined;
        let liquidityFormatted: string | undefined;
        if (userBalance && userBalance > 0n && totalSupply > 0n) {
          userToken0Amount = reserves[0] * userBalance / totalSupply;
          userToken1Amount = reserves[1] * userBalance / totalSupply;
          token0AmountFormatted = formatUnits(userToken0Amount, token0Info.decimals);
          token1AmountFormatted = formatUnits(userToken1Amount, token1Info.decimals);
          liquidityFormatted = formatUnits(userBalance, lpDecimals);
          console.log("[useLiquidityPairData] Computed user amounts:", {
            userBalance: userBalance.toString(),
            totalSupply: totalSupply.toString(),
            token0AmountFormatted,
            token1AmountFormatted,
            liquidityFormatted
          });
        } else {
          console.log("[useLiquidityPairData] No user amounts computed:", {
            userBalance: userBalance?.toString(),
            totalSupply: totalSupply.toString()
          });
        }
        const result = {
          pairAddress: validPairAddress,
          token0: token0Info,
          token1: token1Info,
          reserves: {
            reserve0: reserves[0],
            reserve1: reserves[1],
            blockTimestampLast: reserves[2]
          },
          totalSupply,
          lpName,
          lpSymbol,
          lpDecimals,
          userBalance,
          factoryAddress: factoryAddress ?? undefined,
          dex: dex ?? undefined,
          dexSlug: dexSlug ?? undefined,
          dexName: dex?.name ?? undefined,
          createdAt: createdAt ?? undefined,
          // Computed user amounts
          userToken0Amount,
          userToken1Amount,
          token0AmountFormatted,
          token1AmountFormatted,
          liquidityFormatted
        };
        console.log("[useLiquidityPairData] Final result:", {
          pairAddress: result.pairAddress,
          dexSlug: result.dexSlug,
          dexName: result.dexName,
          userBalance: result.userBalance?.toString(),
          token0AmountFormatted: result.token0AmountFormatted,
          token1AmountFormatted: result.token1AmountFormatted
        });
        return result;
      } catch (error) {
        console.error("Failed to fetch liquidity pair data:", error);
        return null;
      }
    },
    enabled: !!pairAddress && !!chainId && !!publicClient && isAddress(pairAddress as string),
    staleTime: 30_000,
    // 30 seconds
    gcTime: 5 * 60_000 // 5 minutes
  });
}

/**
 * Helper to fetch token info
 */
async function fetchTokenInfo(publicClient: any, tokenAddress: Address): Promise<TokenInfo> {
  try {
    const [symbol, name, decimals] = await Promise.all([publicClient.readContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: "symbol"
    }).catch(() => "UNKNOWN"), publicClient.readContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: "name"
    }).catch(() => "Unknown Token"), publicClient.readContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: "decimals"
    }).catch(() => 18)]);
    return {
      address: tokenAddress,
      symbol: symbol as string,
      name: name as string,
      decimals: decimals as number
    };
  } catch {
    return {
      address: tokenAddress,
      symbol: "UNKNOWN",
      name: "Unknown Token",
      decimals: 18
    };
  }
}
export default useLiquidityPairData;